import { useState } from "react";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { twMerge } from "tailwind-merge";
import { Alert, AlertType } from "@/atoms-react/alert/index.react";
import { Button } from "@/atoms-react/button/Button.react.tsx";
import DotsNavigation from "@/atoms-react/dots-navigation/DotsNavigation.react";
import { Dropdown } from "@/atoms-react/dropdown/index.react";
import Flag from "@/atoms-react/flag/Flag.react";
import InputLabel from "@/atoms-react/input/Input.react";
import { Checkbox } from "@/atoms-react/input-checkbox/index.react";
import { Base, SectionTitleSmall } from "@/atoms-react/text/Text.react.tsx";
import type { DeclarativeNif } from "@/features/flow/types";
import { useNifValidation } from "@/features/user/nif/hooks/use-nif-validation";
import type { ModalIbanAccountStep, QuizStepProps } from "@/features/user/quiz/types";
import { useTaxAddress } from "@/features/user/tax_address/hooks/use-taxAdress";
import type { FullCountry } from "@/features/user/types";
import { COUNTRY_NIF_CHECK_REQUIRED } from "@/features/utils/constants";
import { Themes } from "@/types/theme.ts";

export const NifVerificationStep = ({
  setStep,
  stepSettings,
  user,
  countries,
  setPrevStep,
  totalSteps = 8
}: QuizStepProps) => {
  const { t } = useTranslation(["pass"]);
  const labelInputDivClassName = "md:flex  flex-wrap items-center md:space-x-6";
  const inputClassName = "flex  flex-col grow ";
  const { getNifValidation } = useNifValidation();
  const { mutateAsync: nififValidation, isPending } = getNifValidation();
  const [addressNif, setAddressNif] = useState<DeclarativeNif[]>(
    user?.naturalPerson.TaxAddress ?? []
  );
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string[] | null>([]);
  const { updateUserAddressTaxMutation } = useTaxAddress();
  const {
    mutateAsync: updateTaxAddres,
    isPending: isPendingUpdate,
    isError: updateTaxAddressFailed
  } = updateUserAddressTaxMutation();

  const onSave = async () => {
    setLoading(true);

    const check = addressNif.map(async (ad: DeclarativeNif) => await handleNifVerification(ad));
    try {
      await Promise.all(check);
      setStep(stepSettings.next as ModalIbanAccountStep);
      setLoading(false);
    } catch (_) {
      setLoading(false);
    }
  };
  const isExistsUncheckedResidenceWithoutTin = (() => {
    if (addressNif?.length > 0) {
      const uncheckedNifWithoutTin = addressNif.find(
        (item: DeclarativeNif) =>
          item.country &&
          ((item.tin && item.tin === "") || item.tin === null || !item.tin) &&
          (item.tinCheck === null || item.tinCheck === undefined || item.tinCheck === false)
      );
      const disable = addressNif.every((element) => {
        return (element.tin !== null || element.tin !== "") && element.tinCheck;
      });
      return !!uncheckedNifWithoutTin || disable;
    }
    return true;
  })();
  const handleTaxAddressUpdate = async (ad: DeclarativeNif) => {
    if (!ad?.country || (ad?.country && ad.tin && !ad.apiCheck && !ad.tinCheck)) {
      toast.error(t("flow.id_control.error.must_enter_fields"));
      return;
    }
    try {
      await updateTaxAddres({
        taxAddress: ad
      });
    } catch (_) {
      setError((prev) => [...(prev ?? []), ad.country ?? ""]);
      toast.error(t("olkypass.general.error_save"));
      throw new Error("nif save");
    }
  };

  const handleNifVerification = async (taxResidence: DeclarativeNif) => {
    const countryBirthIsMalta = countries?.countries.find(
      (item: FullCountry) => item.id === user?.naturalPerson?.countryOfBirth && item.alpha2 === "MT"
    );
    const maltaCountryCode = countries?.countries.find(
      (item: FullCountry) => item.alpha2.toLocaleUpperCase() === "MT"
    );
    const taxNifCountry = countries?.countries.find(
      (item: FullCountry) => item.id.toString() === taxResidence.country
    );
    if (
      (taxResidence.tin === null || taxResidence.tin === "") &&
      (taxResidence.tinCheck === undefined ||
        taxResidence.tinCheck === null ||
        taxResidence.tinCheck === false) &&
      !taxResidence.allowUpdate
    ) {
      setError((prev) => [...(prev ?? []), taxResidence.country ?? ""]);
      throw new Error("nif verificaiton");
    }

    if ((taxResidence.tin === null || taxResidence.tin === "") && taxResidence.tinCheck) {
      return;
    } else {
      if (taxResidence.tin && taxResidence.apiCheck && !updateTaxAddressFailed) return;
      else {
        if (
          COUNTRY_NIF_CHECK_REQUIRED.includes(taxNifCountry?.alpha2 ?? "") &&
          ((taxResidence.country ?? "") !== maltaCountryCode?.id.toString() ||
            ((taxResidence.country ?? "") === maltaCountryCode?.id.toString() &&
              countryBirthIsMalta))
        ) {
          const response = await nififValidation({
            nif: taxResidence.tin ?? "",
            countryCode: taxNifCountry?.alpha2 ?? ""
          });
          if (response.validSyntax && response.validStructure) {
            taxResidence.apiCheck = true;
            taxResidence.tinExist = true;
            return await handleTaxAddressUpdate(taxResidence);
          } else {
            setError((prev) => [...(prev ?? []), taxResidence.country ?? ""]);
            throw new Error("nif verificaiton");
          }
        } else {
          taxResidence.apiCheck = true;
          taxResidence.tinExist = true;
          return await handleTaxAddressUpdate(taxResidence);
        }
      }
    }
  };
  return (
    <>
      <SectionTitleSmall className={"mb-2"}>
        {t("olkypass.mainPage.olkypass.modalOpenOlkyPay.steps.nif.title")}{" "}
      </SectionTitleSmall>

      <div className={labelInputDivClassName}>
        <div className={inputClassName}>
          <div className="mt-2 flex grow flex-col">
            {addressNif &&
              addressNif.length > 0 &&
              addressNif.map((ad, indexAd) => (
                <div key={indexAd}>
                  <div className={twMerge("mt-2 flex grow")}>
                    <Dropdown
                      value={ad.country ?? ""}
                      className="maw-w-40 mr-2 w-40 truncate"
                      search={true}
                      disabled={true}
                      items={
                        (countries?.countries ?? []).map((country) => ({
                          key: country.id.toString(),
                          value: country.defaultName,
                          icon: <Flag countryCode={country.alpha2.toLowerCase()} />
                        })) ?? []
                      }
                    />
                    <InputLabel
                      autoComplete="given-name"
                      placeholder={t("olkypass.accountPage.basicInfo.input.nif")}
                      className={`w-full`}
                      error={
                        (ad?.tinExist && ad?.tin?.length === 0 && !ad.apiCheck) ||
                        error?.includes(ad.country?.toString() ?? "")
                          ? t("olkypass.mainPage.olkypass.modalOpenOlkyPay.steps.nif.error")
                          : undefined
                      }
                      value={ad?.tin ?? ""}
                      disabled={!ad?.country || ad?.country === null || ad.apiCheck || isPending}
                      onChange={(value) => {
                        setAddressNif((prev) =>
                          prev.map((af, index) => {
                            if (index === indexAd)
                              return {
                                ...af,
                                tin: value,
                                tinCheck: false,
                                apiCheck: false,
                                allowUpdate: value.length === 0 ? false : true
                              };
                            return af;
                          })
                        );
                      }}
                    />{" "}
                  </div>
                  {(!ad.tin || ad.tin.length === 0 || ad.tin === null) && addressNif.length > 1 && (
                    <div className="mt-2 mb-2">
                      <div className="mt-4 flex w-full flex-row">
                        <Checkbox
                          value={ad?.tinCheck ?? false}
                          onChange={(value) => {
                            setError([]);
                            setAddressNif((prev) =>
                              prev.map((af, index) => {
                                if (index === indexAd)
                                  return {
                                    ...af,
                                    tinCheck: value,

                                    tinExist: false,
                                    apiCheck: false,
                                    allowUpdate:
                                      af?.tinCheck === null || af?.tinCheck === undefined
                                        ? true
                                        : false
                                  };
                                return af;
                              })
                            );
                          }}
                        >
                          <Base className="text-base">
                            {t("olkypass.accountPage.basicInfo.input.nif_checkbox")}
                          </Base>
                        </Checkbox>
                      </div>
                    </div>
                  )}
                </div>
              ))}

            <div className="mt-4">
              <Alert
                type={AlertType.Info}
                title={t("olkypass.mainPage.olkypass.modalOpenOlkyPay.steps.nif.subtitle")}
                className="mt-4"
              >
                <p className="text-slate-400 text-sm dark:text-white">
                  {t("olkypass.mainPage.olkypass.modalOpenOlkyPay.steps.nif.description")}
                </p>
              </Alert>
              <div className="mt-10 mb-4 flex flex-col items-center">
                <DotsNavigation
                  total={totalSteps}
                  current={stepSettings.current}
                  className="mb-4"
                />
              </div>
            </div>

            <div className="flex w-full flex-col-reverse gap-y-2 md:flex-row md:gap-x-2">
              <Button
                theme={Themes.white}
                onClick={() => setPrevStep(stepSettings.previous as ModalIbanAccountStep)}
              >
                {t("olkypass.general.go_back")}
              </Button>

              <Button
                onClick={onSave}
                loading={isPendingUpdate || isPending || loading}
                disabled={isExistsUncheckedResidenceWithoutTin}
              >
                {t("olkypass.general.continue")}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};
